if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mediaVariantMap = this.parseMediaVariantMap();
        this.mediaVariantIdMap = this.parseMediaVariantIdMap();

        // Directly-clickable gallery images (stacked layout) open the modal
        // immediately via modal-opener. Intercept in the capture phase so a
        // click on a different variant's image selects that variant instead
        // of opening the lightbox - see onViewerClick. Independent of the
        // thumbnail strip below, and runs even on layouts (like "stacked")
        // that never render one.
        this.elements.viewer.addEventListener('click', this.onViewerClick.bind(this), true);

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', () => {
            const target = mediaToSwitch.dataset.target;
            if (this.selectVariantForMedia(target)) return;
            this.setActiveMedia(target, false);
          });
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // The "Smak" option's position is resolved server-side, by name, in
      // product-media-gallery.liquid (it must not be assumed to be option1 /
      // index 0 - option order varies by product). That resolved index
      // travels with the map so this file never hardcodes a position either.
      parseMediaVariantMap() {
        const script = this.querySelector('[data-media-variant-map]');
        if (!script) return null;

        const optionIndex = script.dataset.smakOptionIndex;
        if (optionIndex === undefined || optionIndex === '') return null;

        try {
          const entries = JSON.parse(script.textContent);
          if (!Object.keys(entries).length) return null;
          return { entries, optionIndex: parseInt(optionIndex, 10) };
        } catch (error) {
          return null;
        }
      }

      // Media explicitly associated with a variant (per the native Shopify
      // image/variant relationship, see product-media-gallery.liquid) selects
      // that variant through the existing native option input/select +
      // 'change' event flow, the same mechanism compact-flavor-selector.js
      // uses - so price, URL, availability, food-info and the media modal
      // all update through the theme's existing variant-change pipeline
      // rather than duplicating it here. Generic/shared media (absent from
      // the map) is left alone and falls through to the normal
      // setActiveMedia slide switch. Products with no "Smak" option have no
      // map at all (see parseMediaVariantMap), so this always returns false
      // for them and gallery behavior is unchanged.
      //
      // The Smak option's DOM position is not assumed either: option groups
      // (one per product.options_with_values entry - <select> dropdown,
      // swatch or button fieldset, they all share the .product-form__input
      // wrapper) render in the same order Liquid iterates them in, so
      // indexing that NodeList by the Liquid-resolved optionIndex reliably
      // lands on the Smak group regardless of picker type or option order.
      selectVariantForMedia(target) {
        if (!this.mediaVariantMap || !this.dataset.section) return false;

        const rawMediaId = target.replace(`${this.dataset.section}-`, '');
        const flavorValue = this.mediaVariantMap.entries[rawMediaId];
        if (!flavorValue) return false;

        const variantSelects = document.querySelector(`variant-selects[data-section="${this.dataset.section}"]`);
        if (!variantSelects) return false;

        const optionGroup = variantSelects.querySelectorAll('.product-form__input')[this.mediaVariantMap.optionIndex];
        if (!optionGroup) return false;

        const select = optionGroup.querySelector('select');
        if (select) {
          const option = Array.from(select.options).find((opt) => opt.value === flavorValue);
          if (!option || select.value === flavorValue) return false;
          select.value = flavorValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }

        const input = Array.from(optionGroup.querySelectorAll('input')).find((el) => el.value === flavorValue);
        if (!input || input.checked) return false;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      parseMediaVariantIdMap() {
        const script = this.querySelector('[data-media-variant-id-map]');
        if (!script) return null;

        try {
          const entries = JSON.parse(script.textContent);
          return Object.keys(entries).length ? entries : null;
        } catch (error) {
          return null;
        }
      }

      currentVariantId() {
        const form = document.getElementById(`product-form-${this.dataset.section}`);
        return form?.querySelector('input[name="id"]')?.value || null;
      }

      // Directly-clickable gallery images (stacked layout) are each wrapped
      // in their own modal-opener and normally open the lightbox immediately
      // on click. Two distinct actions share that same click, and only one
      // of them should happen:
      //   - a different variant's image -> select that variant; the
      //     lightbox must NOT open for this click (selecting is the whole
      //     point of clicking another flavour's photo, not zooming into it)
      //   - the active variant's own image (or generic/shared media, or a
      //     variant that can't be fully resolved) -> leave the click alone,
      //     the native modal-opener behavior runs and opens the zoom
      // The variant switch itself still goes through the same native input
      // + 'change' flow every other variant control uses (product-info.js's
      // existing pipeline), so price/URL/food-info/etc. all update the
      // normal way - this just decides whether THIS click also opens the
      // modal, without waiting on that switch to finish or reopening the
      // modal afterwards.
      onViewerClick(event) {
        if (!this.mediaVariantIdMap) return;
        const toggle = event.target.closest('.product__media-toggle');
        if (!toggle) return;

        const entry = this.mediaVariantIdMap[toggle.dataset.mediaId];
        if (!entry) return;

        const currentVariantId = this.currentVariantId();
        if (!currentVariantId || String(entry.variantId) === String(currentVariantId)) return;

        const matches = this.resolveVariantInputs(entry.options);
        if (!matches) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.applyVariantInputs(matches);
      }

      // Resolves each of the target variant's own option values (Shopify
      // data, not inferred) to the matching input/option element, without
      // mutating anything yet. Returns null if any option can't be resolved
      // in the current DOM, so the caller can leave the click's default
      // behavior untouched rather than partially applying a variant switch.
      resolveVariantInputs(options) {
        const variantSelects = document.querySelector(`variant-selects[data-section="${this.dataset.section}"]`);
        if (!variantSelects) return null;

        const groups = variantSelects.querySelectorAll('.product-form__input');
        const matches = [];

        for (let i = 0; i < options.length; i++) {
          const group = groups[i];
          if (!group) return null;

          const select = group.querySelector('select');
          if (select) {
            const option = Array.from(select.options).find((opt) => opt.value === options[i]);
            if (!option) return null;
            matches.push({ select, value: options[i] });
            continue;
          }

          const input = Array.from(group.querySelectorAll('input')).find((el) => el.value === options[i]);
          if (!input) return null;
          matches.push({ input });
        }

        return matches;
      }

      // Applies every resolved option at once and dispatches a single
      // 'change' event at the end, so variant-selects recomputes its full
      // selectedOptionValues from the new state and the normal pipeline
      // performs exactly one variant switch - never one change per option.
      applyVariantInputs(matches) {
        let lastChanged = null;
        matches.forEach((match) => {
          if (match.select) {
            if (match.select.value !== match.value) {
              match.select.value = match.value;
              lastChanged = match.select;
            }
          } else if (!match.input.checked) {
            match.input.checked = true;
            lastChanged = match.input;
          }
        });
        if (!lastChanged) return false;
        lastChanged.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
